<template>
  <div class="file-sizes-tab">
    <div class="d-flex align-items-start gap-2 mb-2">
      <div class="mr-2">
        <b-form-input
          v-model="search"
          placeholder="Search templates..."
          class="search-input mb-1"
          :disabled="disabled"
        />
        <b-form-checkbox
          v-model="showOnlySelected"
          class="pl-2"
        >
          Show only selected templates
        </b-form-checkbox>
      </div>

      <div class="d-flex align-items-center gap-2">
        Set all to
        <b-icon
          v-b-tooltip.hover.viewport.noninteractive="'Leave blank to use default sizes.'"
          icon="info-circle"
          class="cursor-pointer"
        />
        <b-form-input
          v-model.number="bulkValue"
          type="number"
          min="1"
          placeholder="Kb"
          class="bulk-input"
          :disabled="disabled || !rows.length"
        />
      </div>
      <b-button
        variant="primary"
        :disabled="disabled || !rows.length || !isValidKb(bulkValue)"
        @click="applyBulk"
      >
        Apply
      </b-button>
      <b-button
        variant="danger"
        :disabled="disabled || !rows.length"
        @click="clearVisible"
      >
        Clear
      </b-button>
    </div>

    <b-table
      small
      responsive
      bordered
      :items="rows"
      :fields="fields"
      :empty-text="emptyText"
    >
      <template #head(max_file_size_kb)>
        Max File Size (Kb)
        <b-icon
          v-b-tooltip.hover.viewport.noninteractive="'Leave blank to use default sizes.'"
          icon="info-circle"
          class="cursor-pointer ml-1"
        />
      </template>

      <template #cell(template_name)="{ item }">
        <div
          :title="item.template_name"
        >
          {{ item.template_name }}
        </div>
      </template>

      <template #cell(max_file_size_kb)="{ item }">
        <b-form-input
          :value="getValue(item.instance_id)"
          type="number"
          min="1"
          size="sm"
          class="kb-input"
          placeholder="Optional"
          :disabled="disabled"
          @input="onInput(item.instance_id, $event)"
        />

        <div
          v-if="hasValue(item.instance_id) && !isValidKb(getValue(item.instance_id))"
          class="text-danger small mt-1"
        >
          Must be a positive number (Kb)
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
/* global previews */

export default {
  name: "ExportFileSizesTab",
  model: { prop: "value", event: "input" },

  props: {
    instances: { type: Array, default: () => [] },

    selectedInstances: { type: Array, default: () => [] },

    value: { type: Object, default: () => ({}) },

    disabled: { type: Boolean, default: false }
  },

  data() {
    return {
      showOnlySelected: true,
      search: "",
      bulkValue: null,
      fields: [
        { key: "template_name", label: "Template Name", tdClass: "align-middle" },
        { key: "max_file_size_kb", label: "Max File Size", thStyle: { width: "0" } }
      ]
    };
  },

  computed: {
    selectedSet() {
      return new Set(
        (this.selectedInstances || [])
          .filter(x => x && String(x.instance_id) !== "-1")
          .map(x => String(x.instance_id))
      );
    },

    normalizedInstances() {
      return (this.instances || [])
        .filter(x => x && String(x.instance_id) !== "-1")
        .map((t) => {
          const instanceId = t.instance_id;

          return {
            instance_id: instanceId,
            template_name: t.name || `Template #${instanceId}`,
            project_name: this.getProjectNameFromPreviews(instanceId) || ""
          };
        });
    },

    rows() {
      let items = this.normalizedInstances;

      if (this.showOnlySelected) {
        items = items.filter(t => this.selectedSet.has(String(t.instance_id)));
      }

      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        items = items.filter(t =>
          String(t.template_name).toLowerCase().includes(q) ||
          String(t.project_name).toLowerCase().includes(q) ||
          String(t.instance_id).includes(q)
        );
      }

      return items;
    },

    emptyText() {
      if (this.showOnlySelected && (!this.selectedInstances || !this.selectedInstances.length)) {
        return "Select templates in the Download dropdown to configure per-template file sizes.";
      }
      return "No templates found.";
    }
  },

  methods: {
    getProjectNameFromPreviews(instanceId) {
      try {
        if (typeof previews === "undefined" || !Array.isArray(previews)) return "";
        const p = previews.find(pr => String(pr.id) === String(instanceId));
        if (!p) return "";

        return (
          p?.project_name ||
          p?.project?.name ||
          p?.template?.project_name ||
          p?.template?.project?.name ||
          ""
        );
      } catch (e) {
        return "";
      }
    },

    getValue(instanceId) {
      const key = String(instanceId);
      return this.value[key] ?? null;
    },

    hasValue(instanceId) {
      const key = String(instanceId);
      return Object.prototype.hasOwnProperty.call(this.value, key);
    },

    isValidKb(v) {
      if (v === null || v === "" || typeof v === "undefined") return false;
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    },

    onInput(instanceId, raw) {
      const key = String(instanceId);
      const next = { ...(this.value || {}) };

      if (raw === "" || raw === null || typeof raw === "undefined") {
        delete next[key];
        this.$emit("input", next);
        return;
      }

      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        next[key] = raw;
        this.$emit("input", next);
        return;
      }

      next[key] = Math.round(n);
      this.$emit("input", next);
    },

    clearOne(instanceId) {
      const key = String(instanceId);
      const next = { ...(this.value || {}) };
      delete next[key];
      this.$emit("input", next);
    },

    applyBulk() {
      if (!this.isValidKb(this.bulkValue)) return;

      const next = { ...(this.value || {}) };
      this.rows.forEach(r => {
        next[String(r.instance_id)] = Math.round(Number(this.bulkValue));
      });
      this.$emit("input", next);
    },

    clearVisible() {
      const next = { ...(this.value || {}) };
      this.rows.forEach(r => {
        delete next[String(r.instance_id)];
      });
      this.$emit("input", next);
    }
  }
};
</script>

<style scoped>
.file-sizes-tab .search-input {
  width: 260px;
}
.file-sizes-tab .bulk-input {
  width: 100px;
}
.file-sizes-tab .kb-input {
  width: 180px;
}
.text-truncate {
  max-width: 360px;
}
</style>
